import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, Car, Plus, HandHelping } from "lucide-react";
import { useTranslation } from "react-i18next";
import { pickColor } from "@/lib/palette";
import { modeOf } from "@/components/ModePicker";

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "OGGI";
  if (diff === 1) return "DOMANI";
  return `${WEEKDAYS[d.getDay()].toUpperCase()} ${d.getDate()} ${MONTHS[d.getMonth()].toUpperCase()}`;
}

function RideRow({ ride, showRole }) {
  const m = modeOf(ride.mode);
  const ModeIcon = m.icon;
  const taken = (ride.passengers || []).length;
  const free = Math.max(0, (ride.seats || 0) - taken);
  const otherName = ride.type === "request" ? (ride.requester_name || "?") : (ride.driver_name || "?");
  const c = pickColor(otherName);
  return (
    <Link
      to={`/rides/${ride.id}`}
      className="block brut-card p-4 hover:border-[#1A2A6C]/30 transition-all"
      data-testid={`rides-list-row-${ride.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="brut-chip" style={{ backgroundColor: `${m.color}1F`, color: m.color }}>
          <ModeIcon className="w-3 h-3" /> {m.label}
        </span>
        <span className="brut-chip bg-[#F5F6FA] text-[#1A2A6C]">{dayLabel(ride.date)} · {ride.time}</span>
      </div>
      {ride.group_name && (
        <p className="text-[11px] uppercase tracking-widest font-bold text-[#8A91A8] mb-2">📍 {ride.group_name}</p>
      )}
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-full bg-[#26B5A8] shrink-0" />
        <span className="text-sm font-bold text-[#26B5A8] truncate">{ride.origin || "—"}</span>
        <span className="flex-1 h-px bg-[#ECEEF5]" />
        <span className="w-2 h-2 rounded-full bg-[#FF4D5E] shrink-0" />
      </div>
      <p className="text-right font-heading text-base font-bold text-[#FF4D5E] mb-3 truncate">{ride.destination}</p>
      <div className="flex items-center justify-between pt-3 border-t border-[#F5F6FA]">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 grid place-items-center rounded-full font-heading font-bold text-sm shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
            {otherName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#1A1D29] truncate">{otherName}</p>
            {showRole && <p className="text-[11px] text-[#8A91A8]">{showRole}</p>}
          </div>
        </div>
        <span className="text-sm font-bold text-[#26B5A8] inline-flex items-center gap-1">
          {ride.type === "offer" ? `${free} liberi` : "Richiesta"}
        </span>
      </div>
    </Link>
  );
}

export default function RidesList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const filter = params.get("filter") || "mine"; // mine | available
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/home");
      let list;
      if (filter === "available") list = data.available_rides || [];
      else if (filter === "requests") list = data.ride_requests || [];
      else list = data.my_upcoming_rides || [];
      setRides(list);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const title = filter === "available"
    ? t("rides.listAvailableTitle")
    : filter === "requests"
    ? t("rides.listRequestsTitle")
    : t("rides.listMineTitle");
  const subtitle = filter === "available"
    ? t("rides.listAvailableSub")
    : filter === "requests"
    ? t("rides.listRequestsSub")
    : t("rides.listMineSub");
  const Icon = filter === "available" ? MapPin : filter === "requests" ? HandHelping : Car;

  const getRole = (r) => {
    if (!user) return null;
    if (r.driver_id === user.id) return "Guidatore";
    if (r.requester_id === user.id) return "Richiedente";
    if ((r.passengers || []).some((p) => p.user_id === user.id)) return "Passeggero";
    return null;
  };

  return (
    <div className="anim-slide-up max-w-2xl mx-auto" data-testid="rides-list-page">
      <Link to="/home" className="text-sm font-bold text-[#1A2A6C] inline-flex items-center gap-1 hover:underline mb-3" data-testid="rides-list-back">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </Link>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#F5F6FA] text-[#1A2A6C]">
          <Icon className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-3xl font-black text-[#1A1D29] leading-tight">{title}</h1>
      </div>
      <p className="text-sm text-[#8A91A8] mb-4">{subtitle}</p>

      {!loading && rides.length > 0 && (
        <div className="mb-5">
          <Link
            to={filter === "available" || filter === "requests" ? "/rides/new?type=request" : "/rides/new"}
            className="brut-btn inline-flex w-full sm:w-auto justify-center"
            data-testid="rides-list-create-button"
          >
            <Plus className="w-4 h-4" /> {filter === "available" || filter === "requests" ? "Richiedi una corsa" : "Crea una corsa"}
          </Link>
        </div>
      )}

      {loading ? (
        <p className="text-center text-[#8A91A8] py-8">Caricamento…</p>
      ) : rides.length === 0 ? (
        <div className="brut-card p-8 text-center">
          <p className="text-[#8A91A8] mb-4">
            {filter === "available"
              ? "Nessuna corsa disponibile al momento."
              : filter === "requests"
              ? "Nessuna richiesta di corsa al momento."
              : "Non hai ancora corse programmate o prenotate."}
          </p>
          <Link to={filter === "available" || filter === "requests" ? "/rides/new?type=request" : "/rides/new"} className="brut-btn inline-flex">
            <Plus className="w-4 h-4" /> {filter === "available" || filter === "requests" ? "Richiedi una corsa" : "Crea una corsa"}
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="rides-list-items">
          {rides.map((r) => (
            <li key={r.id}>
              <RideRow ride={r} showRole={filter === "mine" ? getRole(r) : null} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
